import { useEffect } from 'react';

import {
  DynamicAutocompleteFilterSubmenu,
  FilterCategoryItemControlProps,
  RangeModeFilterSubmenu,
  SwitchFilterRow,
} from '@/components/data-table/filters-v2/index';
import { AutocompleteFilterSubmenu } from '@/components/data-table/filters-v2/submenus/data-table-autocomplete-filter-submenu';
import { DateRangeFilterSubmenu } from '@/components/data-table/filters-v2/submenus/data-table-date-range-filter-submenu';
import {
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';

/**
 * Single filter category item in the dropdown
 */
export function FilterCategoryItem({
  config,
  activeValues,
  onFilterChange,
  onClearFilter,
  activeFilters,
  forceOpen,
  onForceOpenHandled,
  open,
  onOpenChange,
}: FilterCategoryItemControlProps) {
  const activeCount = activeValues.length;

  // Auto-open this submenu when requested (e.g. its chip was clicked)
  useEffect(() => {
    if (forceOpen) {
      onOpenChange(true);
      onForceOpenHandled?.();
    }
  }, [forceOpen, onForceOpenHandled, onOpenChange]);

  // Skip search type filters - they are rendered separately
  if (config.type === 'search') {
    return null;
  }

  // Switch filters render as a flat row, not a submenu
  if (config.type === 'switch') {
    return (
      <SwitchFilterRow
        config={config}
        activeValues={activeValues}
        onFilterChange={onFilterChange}
        onClearFilter={onClearFilter}
      />
    );
  }

  // Locked filter: show the label + active count but make it non-interactive (no submenu).
  if (config.disabled) {
    const lockedRow = (
      <div className="flex w-full cursor-not-allowed items-center justify-between gap-2 px-2 py-1.5 text-sm opacity-50">
        <span>{config.label}</span>
        {activeCount > 0 && (
          <span className="ml-auto flex h-5 min-w-5 items-center justify-center justify-self-end rounded-full bg-foreground px-1.5 text-xs font-medium text-background">
            {activeCount}
          </span>
        )}
      </div>
    );

    if (config.disabledHint) {
      return (
        <SimpleTooltip content={config.disabledHint} side="right">
          {lockedRow}
        </SimpleTooltip>
      );
    }

    return lockedRow;
  }

  const handleCloseSubmenu = () => {
    onOpenChange(false);
  };

  return (
    <DropdownMenuSub open={open} onOpenChange={onOpenChange}>
      <DropdownMenuSubTrigger className="flex w-full cursor-pointer items-center justify-between gap-2 [&_svg]:ml-0">
        <span>{config.label}</span>
        {activeCount > 0 && (
          <span className="ml-auto flex h-5 min-w-5 items-center justify-center justify-self-end rounded-full bg-foreground px-1.5 text-xs font-medium text-background">
            {activeCount}
          </span>
        )}
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="min-w-[220px] p-0">
        {config.type === 'autocomplete' && (
          <AutocompleteFilterSubmenu
            config={config}
            activeValues={activeValues}
            onFilterChange={onFilterChange}
            onClearFilter={onClearFilter}
            onCloseSubmenu={handleCloseSubmenu}
          />
        )}
        {config.type === 'dynamic-autocomplete' && (
          <DynamicAutocompleteFilterSubmenu
            config={config}
            activeValues={activeValues}
            onFilterChange={onFilterChange}
            onClearFilter={onClearFilter}
            onCloseSubmenu={handleCloseSubmenu}
            activeFilters={activeFilters}
          />
        )}
        {config.type === 'rangeMode' && (
          <RangeModeFilterSubmenu
            config={config}
            activeValues={activeValues}
            onFilterChange={onFilterChange}
            onClearFilter={onClearFilter}
            onCloseSubmenu={handleCloseSubmenu}
          />
        )}
        {config.type === 'dateRange' && (
          <DateRangeFilterSubmenu
            config={config}
            activeValues={activeValues}
            onFilterChange={onFilterChange}
            onClearFilter={onClearFilter}
            onCloseSubmenu={handleCloseSubmenu}
          />
        )}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}
